/* ============================================================================
   Liquid glass refraction — the displacement map behind .page-alert.

   The look is Apple's Liquid Glass: whatever sits behind the surface bends
   at its rim as if seen through the curved edge of a lens, and stays flat
   under the middle where the text is. @glass-sdk/liquid-glass does this with
   WebGPU, but it needs React 19 (this app is 18.2) and only refracts its own
   bounded <GlassContent/> layer — it cannot sample an arbitrary page, which
   is all a toast ever floats over.

   So the same optics are done with one SVG filter: feDisplacementMap pushes
   each backdrop pixel by the vector stored in this map's red (x) and green
   (y) channels, 128 being "stay put". Only the bezel — a band `bezel` px wide
   inside the rounded outline — carries displacement, pointing inward along
   the outline's normal. Its size is real refraction through a convex
   squircle edge (lensBend): near zero across most of the band, then rising
   steeply into the last few px, which is what makes the rim read as thick
   glass rather than a soft blur.

   Only Chromium applies an SVG url() inside `backdrop-filter`; Safari and
   Firefox do not, so they get the frosted fallback (blur + saturate) and no
   map is drawn.
   ========================================================================== */

export const supportsRefraction =
  typeof navigator !== "undefined" &&
  /Chrome\//.test(navigator.userAgent) &&
  typeof document !== "undefined";

// refractive index of glass; Snell's law at the surface uses it
const IOR = 1.5;

/* How far light bends passing through the bezel at depth s (0 = outer edge,
   1 = where the flat top begins), normalised so the edge is 1. The surface
   profile is a convex squircle, h(s) = (1 − (1 − s)⁴)^¼: vertical at the
   edge, flat by the inner end. */
const lensBend = (s) => {
  const u = 1 - s;
  const slope = (u * u * u) / Math.pow(Math.max(1e-6, 1 - u * u * u * u), 0.75);
  const incidence = Math.atan(slope);
  return incidence - Math.asin(Math.sin(incidence) / IOR);
};
const EDGE_BEND = Math.PI / 2 - Math.asin(1 / IOR);

/* A data: URL of the map for a w×h box with corner radius `radius`, or null
   when there is no 2D canvas (jsdom). The filter scale that pairs with it is
   2 × the wanted rim displacement in px, because feDisplacementMap offsets
   by scale × (channel / 255 − 0.5). */
export const displacementMap = (w, h, radius, bezel) => {
  const width = Math.round(w);
  const height = Math.round(h);
  if (width < 2 || height < 2) return null;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext && canvas.getContext("2d");
  if (!ctx) return null;

  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  const band = Math.max(1, Math.min(bezel, width / 2, height / 2));
  const hx = width / 2 - r;
  const hy = height / 2 - r;
  const img = ctx.createImageData(width, height);
  const px = img.data;

  for (let y = 0; y < height; y++) {
    const cy = y + 0.5 - height / 2;
    const qy = Math.abs(cy) - hy;
    for (let x = 0; x < width; x++) {
      const cx = x + 0.5 - width / 2;
      const qx = Math.abs(cx) - hx;

      // distance in from the rounded outline, and the outward normal there
      let inset;
      let nx;
      let ny;
      if (qx > 0 && qy > 0) {
        const len = Math.hypot(qx, qy);
        inset = r - len;
        nx = qx / len;
        ny = qy / len;
      } else if (qx > qy) {
        inset = r - qx;
        nx = 1;
        ny = 0;
      } else {
        inset = r - qy;
        nx = 0;
        ny = 1;
      }

      let dx = 0;
      let dy = 0;
      if (inset >= 0 && inset < band) {
        const mag = lensBend(inset / band) / EDGE_BEND;
        // sample inward: the rim shows the backdrop pulled from under the glass
        dx = -Math.sign(cx) * nx * mag;
        dy = -Math.sign(cy) * ny * mag;
      }

      const i = (y * width + x) * 4;
      px[i] = Math.round(128 + dx * 127);
      px[i + 1] = Math.round(128 + dy * 127);
      px[i + 2] = 128;
      px[i + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL();
};
